import { SessionOverrideType } from '@abs/shared-types';
import { getDayOfWeek, parseISODate, formatISODate, todayISO } from './utils/dates';
/**
 * Build a map of overrides by date. If multiple overrides exist for the same date,
 * the last one (by createdAt) wins.
 */
function buildOverrideMap(overrides) {
    const map = new Map();
    // Last override in the array wins for duplicate dates
    for (const override of overrides) {
        map.set(override.date, override);
    }
    return map;
}
/**
 * Build a map of meeting patterns by day-of-week.
 */
function buildPatternMap(patterns) {
    const map = new Map();
    for (const pattern of patterns) {
        // If multiple patterns share a day, last one wins
        map.set(pattern.dayOfWeek, pattern);
    }
    return map;
}
/**
 * Compute all scheduled sessions for a course.
 *
 * Generates dates from startDate to endDate matching meeting patterns,
 * applies overrides (holidays, cancellations, make-ups),
 * and returns both the count and full session array.
 */
export function computeTotalSessions(patterns, overrides, startDate, endDate) {
    if (patterns.length === 0 || startDate > endDate) {
        return { total: 0, sessions: [] };
    }
    const overrideMap = buildOverrideMap(overrides);
    const patternMap = buildPatternMap(patterns);
    const excludedTypes = new Set([SessionOverrideType.Holiday, SessionOverrideType.Cancelled]);
    // Track dates we've already added (to avoid duplicates from MakeUp)
    const addedDates = new Set();
    const sessions = [];
    // 1. Generate all dates matching patterns
    const start = parseISODate(startDate);
    const end = parseISODate(endDate);
    const current = new Date(start);
    while (current <= end) {
        const dateStr = formatISODate(current);
        const dayOfWeek = current.getDay();
        if (patternMap.has(dayOfWeek)) {
            const override = overrideMap.get(dateStr) ?? null;
            // Check if this date is excluded (holiday or cancelled)
            if (override && excludedTypes.has(override.type)) {
                // Skip this session
                current.setDate(current.getDate() + 1);
                addedDates.add(dateStr);
                continue;
            }
            sessions.push({
                date: dateStr,
                override,
                meetingPattern: patternMap.get(dayOfWeek),
            });
            addedDates.add(dateStr);
        }
        current.setDate(current.getDate() + 1);
    }
    // 2. Add make-up sessions that don't match patterns
    for (const override of overrides) {
        if (override.type === SessionOverrideType.MakeUp && !addedDates.has(override.date)) {
            if (override.date >= startDate && override.date <= endDate) {
                sessions.push({
                    date: override.date,
                    override,
                    meetingPattern: null, // Make-up sessions may not follow a regular pattern
                });
            }
        }
    }
    // Sort by date
    sessions.sort((a, b) => a.date.localeCompare(b.date));
    return { total: sessions.length, sessions };
}
/**
 * Count sessions that have already occurred (on or before today).
 */
export function getPastSessionCount(sessions) {
    const today = todayISO();
    return sessions.filter((s) => s.date <= today).length;
}
/**
 * Count sessions that have not yet occurred (after today).
 */
export function getRemainingSessionCount(sessions) {
    const today = todayISO();
    return sessions.filter((s) => s.date > today).length;
}
/**
 * Get sessions that fall within a specific month.
 * @param year - Full year (e.g., 2026)
 * @param month - 0-indexed month (0=January, 11=December)
 */
export function getSessionsForMonth(sessions, year, month) {
    const monthStr = `${year}-${(month + 1).toString().padStart(2, '0')}`;
    return sessions.filter((s) => s.date.startsWith(monthStr));
}
/**
 * Find a meeting pattern for a given date from a set of patterns.
 */
export function getPatternForDate(patterns, date) {
    const dayOfWeek = getDayOfWeek(date);
    return patterns.find((p) => p.dayOfWeek === dayOfWeek) ?? null;
}
//# sourceMappingURL=sessionSchedule.js.map